import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as QueryKeys from "data";
import type { ProductIcon } from "data/models";
import { fetchPlans } from "data/queries-common";
import { CircleCheck, Info, PlusCircle } from "lucide-react";
import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

function FeatureIcon({ icon }: { icon: ProductIcon }) {
  if (icon === "info") return <Info className="text-info size-5" />;
  if (icon === "check") return <CircleCheck className="text-success size-5" />;
  if (icon === "plus") return <PlusCircle className="text-success size-5" />;
  return null;
}

interface FeatureCellProps {
  icon?: ProductIcon;
  value?: React.ReactNode;
  description?: string;
}

function FeatureCell({ icon, value, description }: FeatureCellProps) {
  if (icon) {
    const iconElement = <FeatureIcon icon={icon} />;
    if (description) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex justify-center">{iconElement}</div>
          </TooltipTrigger>
          <TooltipContent>{description}</TooltipContent>
        </Tooltip>
      );
    }
    return <div className="flex justify-center">{iconElement}</div>;
  }
  return <p className="text-center">{value || "-"}</p>;
}

interface FeatureComparisonModalProps {
  open: boolean;
  onClose: () => void;
}

export default function FeatureComparisonModal({
  open,
  onClose,
}: FeatureComparisonModalProps) {
  const plansQuery = useQuery({
    queryKey: [QueryKeys.plans],
    queryFn: () => fetchPlans(),
  });

  const products = plansQuery.data?.products ?? [];

  const categories = useMemo(() => {
    const grouped: Record<string, string[]> = {};
    products.forEach((product) => {
      product.featureBreakdown.forEach((fb) => {
        if (!grouped[fb.category]) grouped[fb.category] = [];
        if (!grouped[fb.category].includes(fb.key)) {
          grouped[fb.category].push(fb.key);
        }
      });
    });
    return grouped;
  }, [products]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto bg-card">
        <DialogHeader>
          <DialogTitle>Feature Comparison</DialogTitle>
        </DialogHeader>

        {plansQuery.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              An error occurred while fetching the features.
            </AlertDescription>
          </Alert>
        )}

        {plansQuery.isPending && (
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        )}

        {plansQuery.isSuccess && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/4" />
                {products.map((product) => (
                  <TableHead
                    key={product.displayName}
                    className="text-center font-semibold"
                  >
                    {product.displayName}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(categories).map(([category, keys]) => (
                <React.Fragment key={category}>
                  <TableRow>
                    <TableCell
                      colSpan={products.length + 1}
                      className="bg-muted/50 text-sm font-semibold"
                    >
                      {category}
                    </TableCell>
                  </TableRow>
                  {keys.map((key) => (
                    <TableRow key={category + key}>
                      <TableCell className="text-muted-foreground">
                        {key}
                      </TableCell>
                      {products.map((product) => {
                        const match = product.featureBreakdown.find(
                          (fb) => fb.category === category && fb.key === key,
                        );
                        return (
                          <TableCell key={product.displayName + key}>
                            <FeatureCell
                              icon={match?.icon}
                              value={match?.value}
                              description={match?.description}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
